import * as React from 'react';
import startCase from 'lodash/startCase';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {getLanguageLabel} from 'src/components/settings/language-support';
import {useSelector, useDispatch} from 'react-redux';
import {classify} from 'src/utils/classify';
import {
  FEATURE_REQUEST_URL,
  CREATE_SUPPORT_TICKET_URL,
  SUPPORT_URL,
} from 'src/utils/app-shell';
import {languageMenuOptions} from 'src/constants/language-support';
import {useHistory} from 'src/rerouter/hooks';
import {getCurrentAgent} from 'src/selectors/accounts';
import {
  selectAgencyData,
  selectAgencyConfigFlag,
  selectBrandingSettings,
} from 'src/selectors/agency';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Avatar} from '@spaced-out/ui-design-system/lib/components/Avatar';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  FormLabelMedium,
  BodySmall,
  TEXT_COLORS,
  ButtonTextSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Truncate} from '@spaced-out/ui-design-system/lib/components/Truncate';
import {CustomDropdown} from 'src/components/lib/custom-dropdown/custom-dropdown';
import {size200} from '@spaced-out/ui-design-system/lib/styles/variables/_size';

import css from './account-bar.css';
import {type MenuOption} from '@spaced-out/ui-design-system';


type AccountBarProps = {
  expanded: boolean;
  role?: string;
  handleSenseUniversityOnClick: () => void;
};

export default function FlippableAvatar({
  text,
  frontImageUrl,
  backImageUrl,
}: {
  text: string;
  frontImageUrl: string;
  backImageUrl: string;
}) {
  return (
    <div className={css.flipCard}>
      <div className={css.flipInner}>
        <div className={css.flipFront}>
          <Avatar
            text={text}
            imageSrc={frontImageUrl}
            classNames={{wrapper: css.avatarImg}}
          />
        </div>
        <div className={css.flipBack}>
          <Avatar
            text={text}
            imageSrc={backImageUrl}
            classNames={{wrapper: css.avatarImg}}
          />
        </div>
      </div>
    </div>
  );
}

export const AccountBar = ({
  expanded,
  handleSenseUniversityOnClick,
}: AccountBarProps) => {
  const dispatch = useDispatch();
  const {t, updateLanguage, getCurrentLanguage} = useLabelTranslation();

  const [activeMenu, setActiveMenu] = React.useState('custom');
  const initialLanguage = getCurrentLanguage({
    supportLocalStorageLanguage: false,
  });
  const [selectedLanguage, setSelectedLanguage] = React.useState({
    key: initialLanguage,
    label: getLanguageLabel(initialLanguage),
  });

  const languageSupportEnabled = useSelector((state) =>
    selectAgencyConfigFlag(state, 'i18NEnableLanguageSupport'),
  );

  const agencyBrandingSettings = useSelector(selectBrandingSettings);
  const agencyAvatarLogo = agencyBrandingSettings.agencyAvatar;

  const accountMenuOptions = [
    {
      key: 'create_support_ticket',
      label: t('Create_a_support_ticket', 'Create a support ticket'),
      iconLeft: 'flag',
    },
    {
      key: 'sense_learning_center',
      label: t('Sense_Learning_Center', 'Sense Learning Center'),
      iconLeft: 'graduation-cap',
    },
    {
      key: 'feature_request',
      label: t('Request_a_feature', 'Request a feature'),
      iconLeft: 'message-dots',
    },
    ...(languageSupportEnabled
      ? [
          {
            key: 'language_support',
            label: `${t('Language', 'Language')} : ${selectedLanguage.label}`,
            iconLeft: 'language',
            iconRight: 'chevron-right',
          },
        ]
      : []),
    {
      key: 'support',
      label: t('Support_Portal', 'Support Portal'),
      iconLeft: 'headset',
    },
    {
      key: 'change_password',
      label: t('Change_password', 'Change password'),
      iconLeft: 'key',
    },
    {
      key: 'signout',
      label: t('Sign_out', 'Sign out'),
      iconLeft: 'arrow-right-from-bracket',
    },
  ];

  const updatedLanguageOptions = languageMenuOptions.map((option) => ({
    ...option,
    iconRight: option.key === selectedLanguage.key ? 'check' : '',
  }));

  const onLanguageSelect = async (option: MenuOption) => {
    await updateLanguage({language: option.key});
    setSelectedLanguage({
      key: option.key,
      label: getLanguageLabel(option.key),
    });
    setActiveMenu('custom');
  };

  const history = useHistory();
  // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'Account | AuthedUserAccount | null | undefined'. | TS2339 - Property 'profilePictureUrl' does not exist on type 'Account | AuthedUserAccount | null | undefined'.
  const {name, profilePictureUrl} = useSelector((state) =>
    getCurrentAgent(state),
  );
  const {name: agencyName} = useSelector(selectAgencyData);
  const handleAccountOptionClick = (option: MenuOption) => {
    if (option.key === 'sense_learning_center') {
      handleSenseUniversityOnClick();
    } else if (option.key === 'change_password') {
      history.push('/reset');
    } else if (option.key === 'feature_request') {
      window.open(FEATURE_REQUEST_URL, '_blank');
    } else if (option.key === 'create_support_ticket') {
      window.open(CREATE_SUPPORT_TICKET_URL, '_blank');
    } else if (option.key === 'support') {
      window.open(SUPPORT_URL, '_blank');
    } else if (option.key === 'signout') {
      history.push('/signout');
    } else if (option.key === 'language_support') {
      setActiveMenu('language');
    }
  };
  return (
    <CustomDropdown
      anchorPosition="right"
      ariaLabel={name}
      menu={{
        isFluid: false,
        menuDisabled: false,
        options:
          activeMenu === 'custom' ? accountMenuOptions : updatedLanguageOptions,
        width: size200,
        selectedKeys: [selectedLanguage.key],
        classNames: {wrapper: css.menuShift},
        header: activeMenu === 'language' && (
          <div className={css.menuHeader}>
            <Button
              type="ghost"
              iconLeftName="chevron-left"
              size="small"
              onClick={() => setActiveMenu('custom')}
            />
            <ButtonTextSmall>
              {t('Choose_Language', 'Choose Language')}
            </ButtonTextSmall>
          </div>
        ),
      }}
      {...(!expanded
        ? {
            tooltip: {
              body: name,
              classNames: {tooltip: css.toolTip},
            },
          }
        : {})}
      onOptionSelect={
        activeMenu === 'language' ? onLanguageSelect : handleAccountOptionClick
      }
      tabIndex={0}
      classNames={{
        dropdownContainer: classify(css.accountDropdownContainer, {
          [css.collapsedDropdownContainer]: !expanded,
        }),
        wrapper: classify(css.accountContainer, {
          [css.collapsedContainer]: !expanded,
        }),
      }}
    >
      <div>
        {agencyAvatarLogo ? (
          <FlippableAvatar
            text={name}
            frontImageUrl={agencyAvatarLogo}
            backImageUrl={profilePictureUrl}
          />
        ) : (
          <Avatar text={name} ring={true} imageSrc={profilePictureUrl} />
        )}
      </div>
      {expanded && (
        <div className={css.nameContainerRow}>
          <div className={css.userDetails}>
            <FormLabelMedium color={TEXT_COLORS.inversePrimary}>
              <Truncate>{name}</Truncate>
            </FormLabelMedium>
            <BodySmall color={TEXT_COLORS.inverseSecondary}>
              <Truncate>{startCase(agencyName)}</Truncate>
            </BodySmall>
          </div>
          <div className={css.arrowWrap}>
            <Icon
              name="chevron-right"
              size="small"
              type="solid"
              color={TEXT_COLORS.inversePrimary}
            />
          </div>
        </div>
      )}
    </CustomDropdown>
  );
};
